import { useFloating, autoUpdate } from '@floating-ui/react-dom';
import { useOutsideClick } from '../useOutsideClick.js';
import { useEscapeCallback } from './useEscapeCallback.js';
import { defaultFloatingPositionMiddleware } from './middleware.js';

/**
 * Hook to position an element (float) to another element (target).
 *
```
  // Example usage
  const { refs, wrapperStyle } = useFloatingPosition({
    active: true,
    outsideClickTrigger: () => console.log('Clicked "outside".')
  })
 
  <button ref={refs.setReference}>Target</button>
  <div ref={refs.setFloat} style={wrapperStyle}>I will be positioned to the button</div>
```
 */
function useFloatingPosition(contextPosition) {
  var _a, _b;
  var options = contextPosition.options,
    _contextPosition$acti = contextPosition.active,
    active = _contextPosition$acti === void 0 ? true : _contextPosition$acti,
    outsideClickTrigger = contextPosition.outsideClickTrigger;
  var mergedOptions = Object.assign({
    middleware: defaultFloatingPositionMiddleware,
    placement: 'bottom-start',
    strategy: 'fixed'
  }, options);
  var fui = useFloating(Object.assign(Object.assign({}, mergedOptions), {
    whileElementsMounted: autoUpdate
  }));
  useEscapeCallback({
    elements: [fui.refs.reference, fui.refs.floating],
    callback: function callback() {
      if (outsideClickTrigger) {
        outsideClickTrigger();
      }
    },
    active: active
  });
  useOutsideClick({
    elements: [fui.refs.reference, fui.refs.floating],
    callback: function callback() {
      if (outsideClickTrigger) {
        outsideClickTrigger();
      }
    },
    active: active
  });
  var wrapperStyle = {
    position: fui.strategy,
    top: (_a = fui.y) !== null && _a !== void 0 ? _a : 0,
    left: (_b = fui.x) !== null && _b !== void 0 ? _b : 0
  };
  return {
    refs: fui.refs,
    wrapperStyle: wrapperStyle
  };
}

export { useFloatingPosition };
